import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { dataService, type UserProfile, type Task, type JournalEntry, type ROIEntry, type Pod, type Achievement, type NotificationItem } from '@/services/dataService';
import { errorHandler, useErrorHandler } from '@/utils/errorHandler';
import { supabase } from '@/integrations/supabase/client';
import { dailyTaskService } from '@/services/dailyTaskService';

export interface SocialActivity {
  id: string;
  user_id: string;
  platform: string;
  activity_type: string;
  description: string;
  due_date: string;
  completed: boolean;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

export const useUserData = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([]);
  const [roiEntries, setROIEntries] = useState<ROIEntry[]>([]);
  const [pods, setPods] = useState<Pod[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [socialActivities, setSocialActivities] = useState<SocialActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Latest idea validation completion timestamp for streak start
  const [ideaValidatedAt, setIdeaValidatedAt] = useState<string | null>(null);

  // Load social activities for a user
  const loadSocialActivities = async (userId: string): Promise<SocialActivity[]> => {
    try {
      // Try to fetch data - will fail gracefully if table doesn't exist
      const { data, error } = await (supabase as any)
        .from('social_activities')
        .select('*')
        .eq('user_id', userId)
        .order('due_date', { ascending: true });

      if (error) {
        // If the error is about the table not existing, log a warning and return empty array
        if (error.code === '42P01') { // 42P01 is the code for "undefined_table"
          console.warn('social_activities table does not exist yet');
          return [];
        }
        console.error('Error fetching social activities:', error);
        return [];
      }

      // Transform data to match the expected interface
      const transformedData = (data || []).map((item: any) => ({
        ...item,
        platform: item.metadata?.platform || 'twitter',
        description: item.content,
        completed: item.status === 'completed'
      }));

      return transformedData as SocialActivity[];
    } catch (error) {
      console.error('Unexpected error in loadSocialActivities:', error);
      return [];
    }
  };

  // Generate periodic notifications for active users
  useEffect(() => {
    const generateNotification = async () => {
      if (!user?.id) return;
      
      try {
        await supabase.functions.invoke('smart-notifications', {
          body: { user_id: user.id }
        });
        
        // Create daily tasks if needed
        await dailyTaskService.createDailyTasksIfNeeded(user.id);
      } catch (error) {
        console.error('Error generating notification or creating daily tasks:', error);
      }
    };

    // Generate initial notification and create daily tasks
    generateNotification();
    
    // Set up interval for periodic notifications (every 15 minutes)
    const interval = setInterval(generateNotification, 15 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [user?.id]);

  const loadUserData = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Load all user data with retry logic
      const loadDataWithRetry = async () => {
        const [
          userProfile,
          userTasks,
          userJournalEntries,
          userROIEntries,
          userPods,
          userAchievements,
          userSocialActivities
        ] = await Promise.all([
          dataService.getUserProfile(user.id),
          dataService.getUserTasks(user.id),
          dataService.getUserJournalEntries(user.id),
          dataService.getUserROIEntries(user.id),
          dataService.getUserPods(user.id),
          dataService.getUserAchievements(user.id),
          loadSocialActivities(user.id)
        ]);

        return {
          userProfile,
          userTasks,
          userJournalEntries,
          userROIEntries,
          userPods,
          userAchievements,
          userSocialActivities
        };
      };

      const data = await errorHandler.withRetry(loadDataWithRetry, { maxAttempts: 2, delay: 1000 });

      // Ensure profile exists
      let ensuredProfile = data.userProfile;
      if (!ensuredProfile) {
        ensuredProfile = await dataService.createUserProfile(user.id, { onboarding_completed: false, tutorial_completed: false });
      }
      setProfile(ensuredProfile);
      // Fetch the latest idea validation timestamp for streak start
      const latestValidation = await dataService.getLatestIdeaAssessmentAt(user.id);
      setIdeaValidatedAt(latestValidation);
      // De-duplicate tasks by id to avoid duplicates in UI
      const uniqueTasks = Array.from(new Map(data.userTasks.map(t => [t.id, t])).values());
      setTasks(uniqueTasks);
      setJournalEntries(data.userJournalEntries);
      setROIEntries(data.userROIEntries);
      setPods(data.userPods);

      // Ensure new users are auto-assigned to the Product Builders pod
      if (user?.id && (data.userPods?.length ?? 0) === 0) {
        const ensured = await dataService.ensureAssignedToProductBuilders(user.id);
        if (ensured) {
          const refreshedPods = await dataService.getUserPods(user.id);
          setPods(refreshedPods);
        }
      } else if (data.userPods?.length > 0) {
        // Fix member count for existing pods that might be 0
        const productBuildersPod = data.userPods.find(p => p.name === 'Product Builders');
        if (productBuildersPod && productBuildersPod.member_count < 104) {
          await supabase
            .from('pods')
            .update({ member_count: 104 })
            .eq('id', productBuildersPod.id);
          
          // Refresh pods to show updated count
          const refreshedPods = await dataService.getUserPods(user.id);
          setPods(refreshedPods);
        } else {
          setPods(data.userPods);
        }
      }

      setAchievements(data.userAchievements);
      setSocialActivities(data.userSocialActivities);

      // Load notifications
      const notifs = await dataService.getUserNotifications(user.id);
      setNotifications(notifs);

      // Initialize user data for new users (no hooks here; plain function to respect Rules of Hooks)
      const initializeNewUser = async () => {
        if (!user?.id) return;

        try {
          setLoading(true);
          await dataService.initializeUserData(user.id);
          // Refresh data after initialization
          await Promise.all([
            dataService.getUserProfile(user.id),
            dataService.getUserTasks(user.id),
            dataService.getUserJournalEntries(user.id),
            dataService.getUserROIEntries(user.id),
            dataService.getUserPods(user.id),
            dataService.getUserAchievements(user.id)
          ]).then(([newProfile, newTasks, newJournalEntries, newROIEntries, newPods, newAchievements]) => {
            setProfile(newProfile);
            const uniqueInitTasks = Array.from(new Map(newTasks.map(t => [t.id, t])).values());
            setTasks(uniqueInitTasks);
            setJournalEntries(newJournalEntries);
            setROIEntries(newROIEntries);
            setPods(newPods);
            setAchievements(newAchievements);
          });
        } catch (error) {
          console.error('Error initializing user data:', error);
          setError('Failed to initialize user data');
        } finally {
          setLoading(false);
        }
      };

      if (data.userProfile && !data.userProfile.tutorial_completed && data.userTasks.length === 0) {
        await initializeNewUser();
      }

    } catch (error) {
      errorHandler.logError(error, 'useUserData.loadUserData');
      setError(errorHandler.getUserMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUserData();
  }, [user]);

  // Auto-award streak badges based on onboarding days worked and refresh achievements
  useEffect(() => {
    const runAwards = async () => {
      if (!user?.id) return;
      try {
        await dataService.checkAndAwardStreakBadges(user.id);
        const latest = await dataService.getUserAchievements(user.id);
        setAchievements(latest);
      } catch (e) {
        // no-op; errors already logged in dataService
      }
    };
    runAwards();
  }, [user?.id]);

  // Helper functions for computed values
  const getSprintDay = () => {
    // Sprint day is now the same as streak days (days since validation)
    return getStreakDays();
  };

  const getStreakDays = () => {
    // Get streak from user profile (database calculates days since idea validation)
    return profile?.streak_days || 0;
  };

  const getDaysRemaining = () => {
    return dataService.calculateDaysRemaining(getStreakDays());
  };

  const getCompletedTasksCount = () => {
    // Count all completed tasks
    return tasks.filter(task => task.completed).length;
  };

  const getPendingTasksCount = () => {
    // Count all pending tasks
    return tasks.filter(task => !task.completed).length;
  };

  const getTotalRevenue = () => {
    return roiEntries
      .filter(entry => entry.type === 'revenue')
      .reduce((sum, entry) => sum + entry.amount, 0);
  };

  const getTotalExpenses = () => {
    return roiEntries
      .filter(entry => entry.type === 'expense')
      .reduce((sum, entry) => sum + entry.amount, 0);
  };

  const getNetProfit = () => {
    return getTotalRevenue() - getTotalExpenses();
  };

  // Task management functions
  const toggleTask = async (taskId: string) => {
    // Optimistically update the UI
    setTasks(prev => prev.map(t => 
      t.id === taskId ? { ...t, completed: !t.completed } : t
    ));

    try {
      // Find the task to get current completion status
      const task = tasks.find(t => t.id === taskId);
      if (!task) return false;

      // Update the task in the database
      const success = await dataService.updateTask(taskId, { 
        ...task,
        completed: !task.completed,
      });

      if (!success) {
        // Revert the optimistic update if the API call fails
        setTasks(prev => prev.map(t => 
          t.id === taskId ? { ...t, completed: task.completed } : t
        ));
        return false;
      }

      // Refresh all data to ensure consistency
      if (user?.id) {
        const [updatedProfile, updatedTasks] = await Promise.all([
          dataService.getUserProfile(user.id),
          dataService.getUserTasks(user.id)
        ]);
        
        if (updatedProfile) setProfile(updatedProfile);
        setTasks(updatedTasks);
      }
      
      return true;
    } catch (error) {
      console.error('Error toggling task:', error);
      // Revert the optimistic update on error
      const task = tasks.find(t => t.id === taskId);
      if (task) {
        setTasks(prev => prev.map(t => 
          t.id === taskId ? { ...t, completed: task.completed } : t
        ));
      }
      return false;
    }
  };

  const addTask = async (taskData: Omit<Task, 'id' | 'created_at' | 'user_id'>) => {
    if (!user) return null;

    const newTask = await dataService.createTask({
      ...taskData,
      user_id: user.id
    });

    if (newTask) {
      setTasks(prev => {
        const map = new Map(prev.map(t => [t.id, t]));
        map.set(newTask.id, newTask);
        return Array.from(map.values());
      });
    }
    return newTask;
  };

  const deleteTask = async (taskId: string) => {
    const success = await dataService.deleteTask(taskId);
    if (success) {
      setTasks(prev => prev.filter(t => t.id !== taskId));
    }
    return success;
  };

  // Journal management functions
  const addJournalEntry = async (entryData: Omit<JournalEntry, 'id' | 'created_at' | 'user_id'>) => {
    if (!user) return null;

    const newEntry = await dataService.createJournalEntry({
      ...entryData,
      user_id: user.id
    });

    if (newEntry) {
      setJournalEntries(prev => [newEntry, ...prev]);
    }
    return newEntry;
  };

  // ROI management functions
  const addROIEntry = async (entryData: Omit<ROIEntry, 'id' | 'created_at' | 'user_id'>) => {
    if (!user) return null;

    const newEntry = await dataService.createROIEntry({
      ...entryData,
      user_id: user.id
    });

    if (newEntry) {
      setROIEntries(prev => [newEntry, ...prev]);
    }
    return newEntry;
  };

  // Profile management functions
  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) return false;

    const success = await dataService.updateUserProfile(user.id, updates);
    if (success) {
      setProfile(prev => prev ? { ...prev, ...updates } : null);
    }
    return success;
  };

  const markTutorialComplete = async () => {
    return await updateProfile({ tutorial_completed: true });
  };

  // Mark tutorial as completed
  const completeTutorial = async () => {
    if (!user?.id) return false;
    
    try {
      const success = await dataService.updateUserProfile(user.id, { tutorial_completed: true });
      if (success) {
        setProfile(prev => prev ? { ...prev, tutorial_completed: true } : null);
      }
      return success;
    } catch (error) {
      console.error('Error completing tutorial:', error);
      return false;
    }
  };

  // Notifications actions
  const markNotificationRead = async (notificationId: string) => {
    const ok = await dataService.markNotificationRead(notificationId);
    if (ok) {
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read_status: true } : n));
    }
    return ok;
  };

  return {
    // Data
    profile,
    tasks,
    journalEntries,
    roiEntries,
    pods,
    achievements,
    notifications,
    socialActivities,
    loading,
    error,
    
    // Computed values
    sprintDay: getSprintDay(),
    streakDays: getStreakDays(),
    daysRemaining: getDaysRemaining(),
    completedTasksCount: getCompletedTasksCount(),
    pendingTasksCount: getPendingTasksCount(),
    totalRevenue: getTotalRevenue(),
    totalExpenses: getTotalExpenses(),
    netProfit: getNetProfit(),
    
    // Actions
    refreshData: loadUserData,
    toggleTask,
    addTask,
    deleteTask,
    addJournalEntry,
    addROIEntry,
    updateProfile,
    markTutorialComplete,
    completeTutorial,
    markNotificationRead,
  };
};
